import type { MeasureId } from './measures.model';
import type { MeasureValues } from './measures.util';

// ---------------------------------------------------------------------------
// Histogram Overlay
// ---------------------------------------------------------------------------

export interface HistogramMarkLine {
  readonly value: number;
  readonly label: string;
  readonly color: string;
  readonly lineStyle: 'solid' | 'dashed';
}

export interface HistogramMarkArea {
  readonly from: number;
  readonly to: number;
  readonly label: string;
  readonly color: string;
}

export interface HistogramOverlay {
  readonly markLines: readonly HistogramMarkLine[];
  readonly markAreas: readonly HistogramMarkArea[];
}

// ---------------------------------------------------------------------------
// Grid Highlight
// ---------------------------------------------------------------------------

export interface GridHighlight {
  /** If set, highlight all data cells with this background color */
  readonly highlightAllColor: string | null;
  /** Specific values to highlight — maps value → background color */
  readonly exactValues: ReadonlyMap<number, string>;
  /** Ranges to highlight (inclusive) with background color */
  readonly ranges: ReadonlyArray<{ readonly min: number; readonly max: number; readonly color: string }>;
}

// ---------------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------------

// Tailwind 500 shades
const COLOR_ORANGE = '#f97316'; // orange-500
const COLOR_ROSE = '#f43f5e'; // rose-500
const COLOR_TEAL = '#14b8a6'; // teal-500
const COLOR_EMERALD = '#10b981'; // emerald-500
const COLOR_VIOLET = '#8b5cf6'; // violet-500
const COLOR_SKY = '#0ea5e9'; // sky-500
const COLOR_CYAN = '#06b6d4'; // cyan-500
// Mark area / overlay alpha fills
const COLOR_ORANGE_ALPHA = 'rgba(249, 115, 22, 0.08)';
const COLOR_VIOLET_ALPHA = 'rgba(139, 92, 246, 0.08)';
const COLOR_SKY_ALPHA = 'rgba(14, 165, 233, 0.08)';

// Grid cell background colors (slightly higher alpha for cell visibility)
const GRID_COLOR_GRAY = 'rgba(156, 163, 175, 0.12)';
const GRID_COLOR_ORANGE = 'rgba(249, 115, 22, 0.12)';
const GRID_COLOR_ROSE = 'rgba(244, 63, 94, 0.12)';
const GRID_COLOR_TEAL = 'rgba(20, 184, 166, 0.12)';
const GRID_COLOR_EMERALD = 'rgba(16, 185, 129, 0.12)';
const GRID_COLOR_VIOLET = 'rgba(139, 92, 246, 0.12)';
const GRID_COLOR_SKY = 'rgba(14, 165, 233, 0.12)';
const GRID_COLOR_CYAN = 'rgba(6, 182, 212, 0.12)';
const GRID_COLOR_INDIGO = 'rgba(99, 102, 241, 0.12)';
const GRID_COLOR_AMBER = 'rgba(251, 191, 36, 0.12)';

// ---------------------------------------------------------------------------
// Builders
// ---------------------------------------------------------------------------

export function buildHistogramOverlay(
  activeMeasures: ReadonlySet<MeasureId>,
  measureValues: MeasureValues,
): HistogramOverlay {
  if (activeMeasures.size === 0 || measureValues.size === 0) {
    return { markLines: [], markAreas: [] };
  }

  const markLines: HistogramMarkLine[] = [];
  const markAreas: HistogramMarkArea[] = [];

  const num = (id: MeasureId) => {
    const v = measureValues.get(id);
    return typeof v === 'number' ? v : 0;
  };

  if (activeMeasures.has('min')) {
    markLines.push({ value: num('min'), label: 'Min', color: COLOR_ORANGE, lineStyle: 'dashed' });
  }
  if (activeMeasures.has('max')) {
    markLines.push({ value: num('max'), label: 'Max', color: COLOR_ORANGE, lineStyle: 'dashed' });
  }
  if (activeMeasures.has('range')) {
    markLines.push({ value: num('min'), label: 'Min', color: COLOR_ORANGE, lineStyle: 'dashed' });
    markLines.push({ value: num('max'), label: 'Max', color: COLOR_ORANGE, lineStyle: 'dashed' });
    markAreas.push({ from: num('min'), to: num('max'), label: 'Range', color: COLOR_ORANGE_ALPHA });
  }
  if (activeMeasures.has('mean')) {
    markLines.push({ value: num('mean'), label: 'μ', color: COLOR_ROSE, lineStyle: 'solid' });
  }
  if (activeMeasures.has('median')) {
    markLines.push({ value: num('median'), label: 'M', color: COLOR_TEAL, lineStyle: 'solid' });
  }
  if (activeMeasures.has('mode')) {
    const mode = measureValues.get('mode');
    if (Array.isArray(mode)) {
      for (const m of mode) {
        markLines.push({ value: m, label: `${m}`, color: COLOR_EMERALD, lineStyle: 'dashed' });
      }
    }
  }
  if (activeMeasures.has('q1')) {
    markLines.push({ value: num('q1'), label: 'Q₁', color: COLOR_VIOLET, lineStyle: 'dashed' });
  }
  if (activeMeasures.has('q3')) {
    markLines.push({ value: num('q3'), label: 'Q₃', color: COLOR_VIOLET, lineStyle: 'dashed' });
  }
  if (activeMeasures.has('iqr')) {
    markLines.push({ value: num('q1'), label: 'Q₁', color: COLOR_VIOLET, lineStyle: 'dashed' });
    markLines.push({ value: num('q3'), label: 'Q₃', color: COLOR_VIOLET, lineStyle: 'dashed' });
    markAreas.push({ from: num('q1'), to: num('q3'), label: 'IQR', color: COLOR_VIOLET_ALPHA });
  }
  if (activeMeasures.has('variance')) {
    const mean = num('mean');
    const sd = num('stddev');
    markLines.push({ value: mean, label: 'μ', color: COLOR_ROSE, lineStyle: 'solid' });
    markLines.push({ value: mean - sd, label: 'μ−σ', color: COLOR_SKY, lineStyle: 'dashed' });
    markLines.push({ value: mean + sd, label: 'μ+σ', color: COLOR_SKY, lineStyle: 'dashed' });
    markAreas.push({ from: mean - sd, to: mean + sd, label: '', color: COLOR_SKY_ALPHA });
  }
  if (activeMeasures.has('stddev')) {
    const mean = num('mean');
    const sd = num('stddev');
    markLines.push({ value: mean, label: 'μ', color: COLOR_ROSE, lineStyle: 'solid' });
    markLines.push({ value: mean - sd, label: 'μ−σ', color: COLOR_CYAN, lineStyle: 'dashed' });
    markLines.push({ value: mean + sd, label: 'μ+σ', color: COLOR_CYAN, lineStyle: 'dashed' });
    markAreas.push({ from: mean - sd, to: mean + sd, label: '', color: COLOR_SKY_ALPHA });
  }
  if (activeMeasures.has('skewness')) {
    markLines.push({ value: num('mean'), label: 'μ', color: COLOR_ROSE, lineStyle: 'solid' });
    markLines.push({ value: num('median'), label: 'M', color: COLOR_TEAL, lineStyle: 'dashed' });
  }

  return { markLines: deduplicateLines(markLines), markAreas };
}

export function buildGridHighlights(
  activeMeasures: ReadonlySet<MeasureId>,
  measureValues: MeasureValues,
): GridHighlight {
  const empty: GridHighlight = { highlightAllColor: null, exactValues: new Map(), ranges: [] };

  if (activeMeasures.size === 0 || measureValues.size === 0) {
    return empty;
  }

  let highlightAllColor: string | null = null;
  const exactValues = new Map<number, string>();
  const ranges: Array<{ readonly min: number; readonly max: number; readonly color: string }> = [];

  const num = (id: MeasureId) => {
    const v = measureValues.get(id);
    return typeof v === 'number' ? v : 0;
  };

  if (activeMeasures.has('count')) {
    highlightAllColor = GRID_COLOR_GRAY;
  }
  if (activeMeasures.has('min')) {
    exactValues.set(num('min'), GRID_COLOR_ORANGE);
  }
  if (activeMeasures.has('max')) {
    exactValues.set(num('max'), GRID_COLOR_ORANGE);
  }
  if (activeMeasures.has('range')) {
    exactValues.set(num('min'), GRID_COLOR_ORANGE);
    exactValues.set(num('max'), GRID_COLOR_ORANGE);
  }
  if (activeMeasures.has('mean')) {
    highlightAllColor = GRID_COLOR_ROSE;
  }
  if (activeMeasures.has('median')) {
    highlightAllColor = GRID_COLOR_TEAL;
  }
  if (activeMeasures.has('variance')) {
    highlightAllColor = GRID_COLOR_SKY;
  }
  if (activeMeasures.has('stddev')) {
    highlightAllColor = GRID_COLOR_CYAN;
  }
  if (activeMeasures.has('skewness')) {
    highlightAllColor = GRID_COLOR_INDIGO;
  }
  if (activeMeasures.has('mode')) {
    const mode = measureValues.get('mode');
    if (Array.isArray(mode)) {
      mode.forEach((v) => exactValues.set(v, GRID_COLOR_EMERALD));
    }
  }
  if (activeMeasures.has('q1')) {
    exactValues.set(num('q1'), GRID_COLOR_VIOLET);
  }
  if (activeMeasures.has('q3')) {
    exactValues.set(num('q3'), GRID_COLOR_VIOLET);
  }
  if (activeMeasures.has('iqr')) {
    ranges.push({ min: num('q1'), max: num('q3'), color: GRID_COLOR_VIOLET });
  }

  return { highlightAllColor, exactValues, ranges };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Chart Selection
// ---------------------------------------------------------------------------

export interface BinSelection {
  readonly dataIndex: number;
  readonly min: number;
  readonly max: number;
}

export function mergeChartSelectionIntoHighlight(
  base: GridHighlight,
  bin: BinSelection | null,
  outlierValue: number | null,
): GridHighlight {
  if (!bin && outlierValue === null) {
    return base;
  }

  const exactValues = new Map(base.exactValues);
  const ranges = [...base.ranges];

  if (bin) {
    ranges.push({ min: bin.min, max: bin.max, color: GRID_COLOR_AMBER });
  }
  if (outlierValue !== null) {
    exactValues.set(outlierValue, GRID_COLOR_AMBER);
  }

  return { highlightAllColor: base.highlightAllColor, exactValues, ranges };
}

export const HOVER_VALUE_CHART_COLOR = '#a5b4fc'; // indigo-300

export function mergeHoveredValueIntoHighlight(base: GridHighlight, hoveredValue: number | null): GridHighlight {
  if (hoveredValue === null) {
    return base;
  }
  const exactValues = new Map(base.exactValues);
  exactValues.set(hoveredValue, GRID_COLOR_INDIGO);
  return { highlightAllColor: base.highlightAllColor, exactValues, ranges: base.ranges };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deduplicateLines(lines: HistogramMarkLine[]): HistogramMarkLine[] {
  const seen = new Map<string, HistogramMarkLine>();
  for (const line of lines) {
    const key = `${line.value}:${line.label}`;
    if (!seen.has(key)) {
      seen.set(key, line);
    }
  }
  return [...seen.values()];
}
